import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RequestService from '../../services/requestService';
import ProcessStepper from '../../components/common/ProcessStepper';
import DateUtils from '../../utils/dateUtils';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { API_BASE_URL } from '../../config/api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import MainLayout from '../../layout/MainLayout';


const ResultsDelivery = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ID aus verschiedenen Quellen extrahieren
  const idFromParams = params.id;
  const idFromState = location.state?.requestId;
  
  // Extrahiere die ID aus der URL, wenn sie als Query-Parameter vorhanden ist
  const urlParams = new URLSearchParams(location.search);
  const queryId = urlParams.get('id');
  
  // ID für die Komponente festlegen
  let id = idFromParams || idFromState || queryId;
  
  // Fallback auf ID 8 für Testzwecke, wenn keine ID gefunden wurde
  if (!id) {
    id = '8'; // ID 8 ist die Rheumatoide Arthritis Studie mit completed Status
    console.log("Keine ID gefunden, verwende Fallback-ID 8 für Testzwecke");
  }
  
  console.log("Verwendete ID:", id, "Typ:", typeof id);
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [deliverySuccess, setDeliverySuccess] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reportMetadata, setReportMetadata] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [markdownContent, setMarkdownContent] = useState(null);
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [availablePlots, setAvailablePlots] = useState([]);

  // Helper function to build authenticated URLs for iframes
  const buildAuthenticatedUrl = (apiPath) => {
    const token = localStorage.getItem('authToken');
    let url;
    if (apiPath.startsWith('http://') || apiPath.startsWith('https://')) {
      // Already a full URL (e.g. from API_BASE_URL)
      url = apiPath;
    } else if (apiPath.startsWith('/api/')) {
      // Relative path with /api/ prefix — strip it since API_BASE_URL includes /api
      url = `${API_BASE_URL}${apiPath.slice(4)}`;
    } else {
      url = `${API_BASE_URL}${apiPath}`;
    }
    // Add token as query parameter for iframe authentication
    return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
  };

  // Laden der Reports vom Django Backend
  useEffect(() => {
    const loadReports = async () => {
      if (!id) return;

      setReportLoading(true);
      try {
        const token = localStorage.getItem('authToken');

        const response = await axios.get(`${API_BASE_URL}/hermes/reports/${id}/direct/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 5000
        });

        console.log('Django Reports geladen:', response.data);

        // Extrahiere Report-Metadaten aus Django API Response
        if (response.data && response.data.length > 0) {
          const latestReport = response.data[0]; // Neuester Report
          const primaryFile = latestReport.files?.find(f => f.is_primary) || latestReport.files?.[0];

          if (primaryFile) {
            setReportMetadata({
              filename: primaryFile.file_name,
              size: `${Math.round(primaryFile.file_size / 1024)} KB`,
              creationDate: new Date(latestReport.generated_at).toLocaleDateString('de-DE'),
              reportId: latestReport.id,
              filePath: primaryFile.relative_path  // Use relative_path for direct download
            });
          }

          // Setze alle verfügbaren Dateien für die Anzeige (Direct Submodule Paths)
          const allFiles = latestReport.files || [];
          setAvailablePlots(allFiles.filter(f => f.file_type === 'html').map(f => ({
            filename: f.file_name,
            url: `${API_BASE_URL}/hermes/reports/${id}/direct/download/${f.relative_path}`,
            type: f.file_type
          })));
        }
      } catch (error) {
        console.error('Fehler beim Laden der Django Reports:', error);
        // Fehler nicht anzeigen, da möglicherweise kein Report verfügbar ist
      } finally {
        setReportLoading(false);
      }
    };

    loadReports();
  }, [id]);
  
  // Lade Markdown-Report von Django API (wie in der Express API)
  useEffect(() => {
    const loadMarkdownReport = async () => {
      if (!id) return;

      setMarkdownLoading(true);
      try {
        const token = localStorage.getItem('authToken');

        const response = await axios.get(`${API_BASE_URL}/hermes/reports/${id}/report-markdown/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 5000
        });

        console.log('Django Markdown Report geladen:', response.data);

        if (response.data && response.data.content) {
          setMarkdownContent(response.data.content);
        } else {
          setMarkdownContent(null);
        }
      } catch (error) {
        console.error('Fehler beim Laden des Markdown Reports:', error);

        // Fallback: Erstelle einfache Markdown-Struktur basierend auf verfügbaren Plots
        if (availablePlots.length > 0) {
          const fallbackMarkdown = [
            {
              type: 'heading1',
              title: 'Studienergebnisse - Datenanalyse',
              content: [
                'Diese Visualisierungen zeigen die wichtigsten Erkenntnisse aus der durchgeführten Studie.',
                'Alle Diagramme sind interaktiv und bieten detaillierte Einblicke in die Datenanalyse.'
              ],
              plots: availablePlots.map(plot => ({
                src: plot.url,
                altText: plot.filename.replace(/\.[^/.]+$/, "").replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                apiUrl: plot.url,
                filename: plot.filename
              }))
            }
          ];
          setMarkdownContent(fallbackMarkdown);
        } else {
          setMarkdownContent(null);
        }
      } finally {
        setMarkdownLoading(false);
      }
    };

    loadMarkdownReport();
  }, [id, availablePlots]);

  // Laden der Auftragsdaten
  useEffect(() => {
    const loadOrder = async () => {
      try {
        // Bestimme die zu verwendende Request-ID
        let requestId;
        
        // Wenn eine ID verfügbar ist, verwende diese
        if (id) {
          requestId = parseInt(id, 10);
          console.log("ID verfügbar:", requestId, typeof requestId);
          
          // Zusätzliche Prüfung, ob die ID gültig ist
          if (isNaN(requestId)) {
            console.error("ID konnte nicht in eine Zahl umgewandelt werden:", id);
            setLoading(false);
            return;
          }
        } else {
          console.error("Keine gültige ID gefunden");
          setLoading(false);
          return;
        }
        
        console.log("Lade Anfrage mit ID:", requestId);
        const requestData = await RequestService.getRequestById(requestId);
        console.log("Geladene Anfragedaten:", requestData);
        
        if (requestData) {
          // Konvertiere die Anfragedaten in das Format für die Ergebnisübermittlung
          const orderData = {
            id: requestData.id,
            title: requestData.title,
            company: requestData.company,
            date: requestData.date,
            completionDate: requestData.results?.completionDate || new Date().toLocaleDateString('de-DE'),
            status: requestData.status,
            statusHistory: requestData.statusHistory || {},
            price: requestData.price || 4000,
            patientCount: requestData.patientCount || 100,
            paymentStatus: requestData.results?.paymentStatus || 'pending',
            description: requestData.description || 'Keine Beschreibung verfügbar.'
          };
          
          // Wenn die Anfrage bereits Ergebnisdaten hat, verwende diese
          if (requestData.results) {
            orderData.results = requestData.results.files || [];
            orderData.keyFindings = requestData.results.keyFindings || [];
          } else {
            // Ansonsten erstelle Standardergebnisse
            orderData.results = [
              {
                id: 1,
                name: 'Hauptbericht',
                type: 'pdf',
                size: '2.4 MB',
                date: new Date().toLocaleDateString('de-DE'),
                description: 'Vollständiger Analysebericht mit allen Ergebnissen und Methodik.'
              },
              {
                id: 2,
                name: 'Statistische Auswertung',
                type: 'xlsx',
                size: '1.8 MB',
                date: new Date().toLocaleDateString('de-DE'),
                description: 'Detaillierte statistische Auswertungen in Tabellenform.'
              },
              {
                id: 3,
                name: 'Diagramme',
                type: 'pptx',
                size: '3.5 MB',
                date: new Date().toLocaleDateString('de-DE'),
                description: 'Visualisierungen der wichtigsten Ergebnisse als Präsentation.'
              },
              {
                id: 4,
                name: 'Rohdaten (anonymisiert)',
                type: 'csv',
                size: '5.2 MB',
                date: new Date().toLocaleDateString('de-DE'),
                description: 'Anonymisierte Rohdaten für weiterführende Analysen.'
              }
            ];
            
            orderData.keyFindings = [
              'Signifikante Korrelation zwischen wichtigen Parametern (p<0.001)',
              'Patienten mit Risikofaktoren haben ein erhöhtes Risiko für Komplikationen',
              'Identifizierte Biomarker zeigen hohe Spezifität und Sensitivität',
              'Die untersuchte Therapie zeigt signifikante Verbesserungen im Vergleich zur Standardbehandlung'
            ];
          }
          
          setOrder(orderData);
          setLoading(false);
        } else {
          console.error("Keine Anfrage mit ID", requestId, "gefunden");
          setLoading(false);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Anfrage:", error);
        setLoading(false);
      }
    };
    
    loadOrder();
  }, [id]);

  const handleDeliveryConfirm = () => {
    // In einer realen Anwendung würde hier die Übermittlung an die API gesendet
    setDeliveryDialogOpen(false);
    setDeliverySuccess(true);
    setOrder({
      ...order,
      status: 'delivered'
    });
  };

  const handlePreviewResult = (result) => {
    setSelectedResult(result);
    setPreviewDialogOpen(true);
  };
  
  const handleDownloadReport = () => {
    if (!id || !reportMetadata) return;

    const token = localStorage.getItem('authToken');

    // Verwende Django Direct Submodule API Endpoint zum Download
    const reportUrl = `${API_BASE_URL}/hermes/reports/${id}/direct/download/${reportMetadata.filePath}?token=${token}`;

    // Öffne den Report in einem neuen Tab
    window.open(reportUrl, '_blank');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon color="error" />;
      default:
        return <FileIcon color="primary" />;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Der angeforderte Auftrag konnte nicht gefunden werden.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 0, pt: 0, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <ProcessStepper 
            activeStep={4} 
            title="Bearbeitungsstatus der Anfrage" 
            requestStatus={order.status} 
            requestId={order.id}
          />
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <Typography variant="overline" display="block" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 0.5 }}>
              ERGEBNISÜBERMITTLUNG
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {order.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              Anfrage von {order.company} • Eingangsdatum: {DateUtils.formatDate(order.date)}
              {order.statusHistory && order.statusHistory.completed && (
                <Chip 
                  size="small" 
                  label={`Bearbeitungszeitraum: ${DateUtils.formatProcessingPeriod(order.statusHistory.completed.startDate, order.statusHistory.completed.endDate)}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2, height: 24, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
          </Box>
        </Paper>

        {deliverySuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Die Ergebnisse wurden erfolgreich an {order.company} übermittelt. Die Zahlung kann nun veranlasst werden.
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/incoming-requests')}
            startIcon={<ChevronLeftIcon />}
          >
            Zurück zur Übersicht
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              icon={<CheckCircleIcon fontSize="small" />}
              label={`Status: ${order.status === 'delivered' ? 'Übermittelt' : order.status === 'completed' ? 'Abgeschlossen' : order.status}`} 
              color={order.status === 'delivered' ? 'success' : 'primary'} 
              variant="outlined" 
              sx={{ fontWeight: 'medium' }}
            />
            <Chip 
              label={`Patienten: ${order.patientCount}`} 
              color="primary" 
              variant="outlined" 
              sx={{ fontWeight: 'medium' }}
            />
          </Box>
        </Box>
        
        <Box sx={{ width: '100%', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Diagramme & Visualisierungen" />
            <Tab label="Dateien & Übermittlung" />
          </Tabs>
        </Box>

        {activeTab === 0 ? (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Studienergebnisse - Visualisierungen
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Hier werden die wichtigsten Ergebnisse der Studie visualisiert.
            </Typography>
            <Grid container spacing={3}>
              {markdownLoading ? (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              ) : markdownContent ? (
                // Markdown-Inhalt anzeigen
                <>
                  {markdownContent.map((section, index) => (
                    <Grid item xs={12} key={index}>
                      <Card 
                        elevation={section.type === 'heading1' ? 2 : 1} 
                        sx={{ 
                          mb: 3,
                          bgcolor: section.type === 'heading1' ? '#f8f9fa' : 
                                  section.type === 'heading2' ? '#ffffff' : '#fafafa',
                          borderLeft: section.type === 'heading1' ? '4px solid #1976d2' : 
                                     section.type === 'heading2' ? '3px solid #42a5f5' : 
                                     '2px solid #90caf9'
                        }}
                      >
                        <CardHeader 
                          title={
                            <Typography 
                              variant={section.type === 'heading1' ? 'h5' : 
                                      section.type === 'heading2' ? 'h6' : 'subtitle1'}
                              sx={{ 
                                fontWeight: section.type === 'heading1' ? 'bold' : 
                                          section.type === 'heading2' ? 'medium' : 'normal' 
                              }}
                            >
                              {section.title}
                            </Typography>
                          }
                          subheader={
                            <Chip 
                              size="small" 
                              label={section.type === 'heading1' ? 'Hauptabschnitt' : 
                                    section.type === 'heading2' ? 'Unterabschnitt' : 'Detailabschnitt'}
                              color={section.type === 'heading1' ? 'primary' : 
                                    section.type === 'heading2' ? 'info' : 'default'}
                              variant={section.type === 'heading3' ? 'outlined' : 'filled'}
                              sx={{ mt: 1 }}
                            />
                          }
                        />
                        <CardContent>
                          {section.content.length > 0 && (
                            <Box sx={{ 
                              mb: 2, 
                              pl: section.type === 'heading2' ? 2 : 
                                  section.type === 'heading3' ? 3 : 0,
                              borderLeft: section.type === 'heading3' ? '1px solid #e0e0e0' : 'none'
                            }}>
                              <div className="markdown-content" 
                                dangerouslySetInnerHTML={{ 
                                  __html: section.content.join('\n')
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\*(.*?)\*\*/g, '<em>$1</em>')
                                    .replace(/\n/g, '<br />')
                                    .replace(/^- (.*)/gm, '• $1')
                                    .replace(/^(\d+)\. (.*)/gm, '$1. $2')
                                }} 
                              />
                            </Box>
                          )}
                          
                          {section.plots.length > 0 && (
                            <Grid container spacing={2}>
                              {section.plots.map((plot, plotIndex) => {
                                // Finde den entsprechenden Plot in availablePlots
                                const matchingPlot = availablePlots.find(p => 
                                  p.filename.includes(plot.src.replace(/\.\.\//g, '').replace(/\.\//g, ''))
                                );
                                
                                return (
                                  <Grid item xs={12} key={plotIndex}>
                                    <Box 
                                      sx={{ 
                                        border: '1px solid #eee', 
                                        borderRadius: 1, 
                                        p: 2, 
                                        height: 'auto',
                                        minHeight: 650,
                                        maxHeight: 800,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      {plot.apiUrl ? (
                                        <>
                                          <Typography variant="subtitle2" gutterBottom>
                                            {plot.altText}
                                          </Typography>
                                          {plot.fileType === 'html' ? (
                                            // HTML-Plots als iframe laden
                                            <Box
                                              component="iframe"
                                              src={buildAuthenticatedUrl(plot.apiUrl)}
                                              title={plot.altText}
                                              sx={{
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 600,
                                                border: 'none'
                                              }}
                                              onError={(e) => {
                                                console.error("Fehler beim Laden des HTML-Plots:", plot.apiUrl);
                                                // Fallback für SVG falls HTML nicht funktioniert
                                                if (plot.svgFilename) {
                                                  const svgUrl = plot.apiUrl.replace(plot.filename, plot.svgFilename);
                                                  const imgElement = document.createElement('img');
                                                  imgElement.src = buildAuthenticatedUrl(svgUrl);
                                                  imgElement.alt = plot.altText;
                                                  imgElement.style.maxWidth = '100%';
                                                  imgElement.style.maxHeight = '700px';
                                                  imgElement.style.objectFit = 'contain';
                                                  e.target.parentNode.replaceChild(imgElement, e.target);
                                                } else {
                                                  e.target.style.display = 'none';
                                                  const errorMsg = document.createElement('div');
                                                  errorMsg.textContent = "Diagramm konnte nicht geladen werden";
                                                  errorMsg.style.color = '#666';
                                                  errorMsg.style.padding = '20px';
                                                  errorMsg.style.textAlign = 'center';
                                                  e.target.parentNode.appendChild(errorMsg);
                                                }
                                              }}
                                            />
                                          ) : (
                                            // SVG/andere Dateien als img laden
                                            <Box
                                              component="img"
                                              src={buildAuthenticatedUrl(plot.apiUrl)}
                                              alt={plot.altText}
                                              sx={{
                                                maxWidth: '100%',
                                                maxHeight: 700,
                                                objectFit: 'contain'
                                              }}
                                            />
                                          )}
                                        </>
                                      ) : matchingPlot ? (
                                        <>
                                          <Typography variant="subtitle2" gutterBottom>
                                            {plot.altText}
                                          </Typography>
                                          {matchingPlot.filename.endsWith('.svg') ? (
                                            <Box
                                              component="img"
                                              src={buildAuthenticatedUrl(matchingPlot.url)}
                                              alt={plot.altText}
                                              sx={{
                                                maxWidth: '100%',
                                                maxHeight: 700,
                                                objectFit: 'contain'
                                              }}
                                            />
                                          ) : (
                                            <Box
                                              component="iframe"
                                              src={buildAuthenticatedUrl(matchingPlot.url)}
                                              title={plot.altText}
                                              sx={{
                                                width: '100%',
                                                height: 700,
                                                border: 'none'
                                              }}
                                            />
                                          )}
                                        </>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          Plot nicht verfügbar: {plot.originalSrc || plot.src}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </>
              ) : (
                // Fallback, wenn kein Markdown-Inhalt verfügbar ist
                <>
                  <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                      <CardHeader title="Diagramm 1" subheader="Primärer Endpunkt" />
                      <CardContent>
                        <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                          <Typography variant="body2" color="text.secondary">
                            Diagramm wird geladen...
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                      <CardHeader title="Diagramm 2" subheader="Sekundärer Endpunkt" />
                      <CardContent>
                        <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                          <Typography variant="body2" color="text.secondary">
                            Diagramm wird geladen...
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
              
              {/* PDF Report Anzeige */}
              {reportMetadata && (
                <Grid item xs={12}>
                  <Card elevation={2}>
                    <CardHeader 
                      title="Finaler Report (PDF)" 
                      subheader={`Erstellt am: ${reportMetadata.creationDate} • Größe: ${reportMetadata.size}`}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 3 }}>
                        <PdfIcon sx={{ fontSize: 80, color: '#e53935', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          {reportMetadata.filename}
                        </Typography>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadReport}
                          sx={{ mt: 2 }}
                        >
                          PDF Report herunterladen
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {reportLoading && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        ) : (
          <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader title="Auftragsdetails" />
              <Divider />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {order.title}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Unternehmen:</strong> {order.company}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Eingangsdatum:</strong> {DateUtils.formatDate(order.date)}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Abschlussdatum:</strong> {order.completionDate}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Patientenzahl:</strong> {order.patientCount}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Preis:</strong> {order.price} €
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1">
                    <strong>Status:</strong>
                  </Typography>
                  <Chip 
                    label={order.status === 'completed' ? 'Abgeschlossen' : 
                          order.status === 'delivered' ? 'Übermittelt' : order.status} 
                    color={order.status === 'completed' || order.status === 'delivered' ? 'success' : 'default'} 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">
                    <strong>Zahlung:</strong>
                  </Typography>
                  <Chip 
                    label={order.paymentStatus === 'pending' ? 'Ausstehend' : 
                          order.paymentStatus === 'completed' ? 'Bezahlt' : order.paymentStatus} 
                    color={order.paymentStatus === 'completed' ? 'success' : 'warning'} 
                  />
                </Box>
              </CardContent>
            </Card>

            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader 
                title="Wichtigste Ergebnisse" 
                action={
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              />
              <Divider />
              <CardContent>
                <List dense>
                  {order.keyFindings.map((finding, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={finding} />
                    </ListItem>
                  ))}
                </List>
                <Button 
                  startIcon={<AddIcon />} 
                  sx={{ mt: 1 }}
                  size="small"
                >
                  Ergebnis hinzufügen
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Ergebnisdateien
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                >
                  Datei hinzufügen
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Datei</TableCell>
                      <TableCell>Beschreibung</TableCell>
                      <TableCell>Größe</TableCell>
                      <TableCell>Datum</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Hauptbericht (PDF) aus der API */}
                    {reportMetadata && (
                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PdfIcon color="error" />
                            <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                              Hauptbericht (PDF)
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>Finaler Report mit allen Studienergebnissen und Analysen</TableCell>
                        <TableCell>{reportMetadata.size}</TableCell>
                        <TableCell>{reportMetadata.creationDate}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Vorschau">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                // Erstelle eine URL für Django API
                                const token = localStorage.getItem('authToken');
                                const pdfUrl = `${API_BASE_URL}/hermes/reports/${id}/direct/download/${reportMetadata.filePath}?token=${token}`;

                                setSelectedResult({
                                  name: "Hauptbericht (PDF)",
                                  type: "pdf",
                                  size: reportMetadata.size,
                                  date: reportMetadata.creationDate,
                                  description: "Finaler Report mit allen Studienergebnissen und Analysen",
                                  url: pdfUrl,
                                  viewerUrl: `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`
                                });
                                setPreviewDialogOpen(true);
                              }} 
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Herunterladen">
                            <IconButton size="small" onClick={handleDownloadReport} color="primary">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )}
                
                    {/* Andere Ergebnisdateien */}
                    {order.results.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getFileIcon(result.type)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {result.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{result.description}</TableCell>
                        <TableCell>{result.size}</TableCell>
                        <TableCell>{result.date}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Vorschau">
                            <IconButton size="small" onClick={() => handlePreviewResult(result)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Herunterladen">
                            <IconButton size="small">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Löschen">
                            <IconButton size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Ergebnisübermittlung
              </Typography>
              <Typography variant="body1" paragraph>
                Nachdem alle Ergebnisse überprüft wurden, können diese an {order.company} übermittelt werden.
                Nach der Übermittlung wird der Kunde benachrichtigt und kann die Zahlung veranlassen.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Vor der Übermittlung müssen Sie bestätigen, dass die Informationen den vertraglichen Vereinbarungen entsprechen.
              </Alert>
              
              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Checkbox 
                    checked={confirmationChecked} 
                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                    color="primary"
                    id="confirmation-checkbox-main"
                  />
                  <Box component="label" htmlFor="confirmation-checkbox-main" sx={{ ml: 1 }}>
                    <Typography variant="body2">
                      Ich bestätige hiermit, dass die Informationen in diesem Report den vertraglichen Vereinbarungen entsprechen 
                      und der Report für die Übermittlung an den Anfragenden nach Zahlung freigegeben werden kann.
                    </Typography>
                  </Box>
                </Box>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => setConfirmationChecked(!confirmationChecked)}
                  sx={{ mt: 1 }}
                >
                  {confirmationChecked ? 'Bestätigung zurückziehen' : 'Vertragsbedingungen bestätigen'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={() => setDeliveryDialogOpen(true)}
                  disabled={order.status === 'delivered' || deliverySuccess || !confirmationChecked}
                >
                  Ergebnisse übermitteln
                </Button>
                <Button 
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadReport}
                  disabled={!reportMetadata}
                >
                  {reportMetadata ? 'Hauptbericht herunterladen' : 'Kein Report verfügbar'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        )}

        {/* Dialog für Ergebnisübermittlung */}
        <Dialog
          open={deliveryDialogOpen}
          onClose={() => setDeliveryDialogOpen(false)}
          maxWidth="md"
        >
          <DialogTitle>Ergebnisse übermitteln</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Sie sind dabei, die Ergebnisse an {order.company} zu übermitteln. 
              Nach der Übermittlung wird der Kunde benachrichtigt und kann die Zahlung veranlassen.
            </DialogContentText>
            
            <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Bestätigung der Vertragsbedingungen
              </Typography>
              <Typography variant="body2" paragraph>
                Bitte bestätigen Sie, dass die folgenden Bedingungen erfüllt sind:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Checkbox 
                  checked={confirmationChecked} 
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  color="primary"
                  id="confirmation-checkbox"
                />
                <Box component="label" htmlFor="confirmation-checkbox" sx={{ ml: 1 }}>
                  <Typography variant="body2">
                    Ich bestätige hiermit, dass die Informationen in diesem Report den vertraglichen Vereinbarungen entsprechen 
                    und der Report für die Übermittlung an den Anfragenden nach Zahlung freigegeben werden kann. 
                    Alle Daten wurden gemäß den Datenschutzrichtlinien anonymisiert und die Ergebnisse entsprechen 
                    den wissenschaftlichen Standards.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <TextField
              margin="dense"
              label="Begleitnotiz (optional)"
              fullWidth
              multiline
              rows={4}
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeliveryDialogOpen(false)}>Abbrechen</Button>
            <Button 
              onClick={handleDeliveryConfirm} 
              variant="contained" 
              color="primary"
              disabled={!confirmationChecked}
            >
              Ergebnisse übermitteln
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog für Dateivorschau */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          {selectedResult && (
            <>
              <DialogTitle>{selectedResult.name}</DialogTitle>
              <DialogContent>
                {selectedResult.type === 'pdf' && selectedResult.viewerUrl ? (
                  <Box sx={{ height: '70vh', width: '100%' }}>
                    <iframe 
                      src={selectedResult.viewerUrl}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        border: 'none',
                        borderRadius: '4px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                      title={selectedResult.name}
                      frameBorder="0"
                      sandbox="allow-scripts allow-same-origin allow-forms"
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', bgcolor: '#f5f5f5' }}>
                    {selectedResult.type === 'pdf' ? (
                      <PdfIcon sx={{ fontSize: 120, color: '#e53935' }} />
                    ) : (
                      <FileIcon sx={{ fontSize: 120, color: '#1976d2' }} />
                    )}
                  </Box>
                )}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Beschreibung:</strong> {selectedResult.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Typ:</strong> {selectedResult.type.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Größe:</strong> {selectedResult.size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Datum:</strong> {selectedResult.date}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPreviewDialogOpen(false)}>Schließen</Button>
                {selectedResult.url && (
                  <Button 
                    variant="contained" 
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(selectedResult.url, '_blank')}
                  >
                    Herunterladen
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default ResultsDelivery;
